/**
 * One message row in a conversation stream. Purely presentational: `variant`
 * picks the side + color (sent = the user, received = the companion), the
 * caller supplies already-rendered content, a pre-formatted timestamp, and an
 * optional avatar/status slot. No message model, no markdown, no time
 * formatting lives here — see `formatDateTime` for that.
 */
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import { Spinner } from './Spinner.tsx';

export type ChatBubbleVariant = 'sent' | 'received';
export type ChatBubbleStatus = 'sending' | 'sent' | 'failed';

const STYLE_ID = 'f4-chat-bubble';
const CSS = `
.f4-chat-row {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  animation: f4-slide-up var(--duration-fast, 150ms) var(--ease-decelerate, ease);
}
.f4-chat-row--sent { flex-direction: row-reverse; }
.f4-chat-row__bubble {
  max-width: 32rem;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  color: var(--color-text);
  background: var(--color-surface-raised);
}
.f4-chat-row--sent .f4-chat-row__bubble {
  color: var(--color-text-on-accent);
  background: var(--color-accent);
}
.f4-chat-row--failed .f4-chat-row__bubble { border: 1px solid var(--color-danger); }
.f4-chat-row__meta {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-text-subtle);
}
.f4-chat-row--sent .f4-chat-row__meta { justify-content: flex-end; }
.f4-chat-row__status--failed { color: var(--color-danger); }
@media (prefers-reduced-motion: reduce) {
  .f4-chat-row { animation: none; }
}
`;

interface ChatBubbleBaseProps {
  /** Which side of the stream this message renders on. */
  readonly variant: ChatBubbleVariant;
  /** Message content — text, markdown, or generative UI; caller's choice. */
  readonly children: JSX.Element;
  /** Avatar slot, e.g. an `<Avatar />`. */
  readonly avatar?: JSX.Element;
  /** Pre-formatted timestamp, e.g. via `formatDateTime`. */
  readonly timestamp?: JSX.Element;
  readonly class?: string;
}

/**
 * Status is a discriminated contract, not a documentation promise: a rendered
 * `sending`/`failed` status MUST carry a non-empty `statusLabel` — it becomes
 * the Spinner's `aria-label` and the failure text — while `sent` (timestamp
 * only) and the no-status case forbid it. An empty accessible name is thus
 * unrepresentable.
 */
export type ChatBubbleStatusProps =
  | { readonly status?: undefined; readonly statusLabel?: undefined }
  | { readonly status: 'sent'; readonly statusLabel?: undefined }
  | { readonly status: 'sending' | 'failed'; readonly statusLabel: string };

export type ChatBubbleProps = ChatBubbleBaseProps & ChatBubbleStatusProps;

export function ChatBubble(props: ChatBubbleProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  return (
    <div
      class={cx(
        'f4-chat-row',
        `f4-chat-row--${props.variant}`,
        props.status === 'failed' && 'f4-chat-row--failed',
        props.class,
      )}
    >
      <Show when={props.avatar}>{props.avatar}</Show>
      <div>
        <div class="f4-chat-row__bubble">{props.children}</div>
        <Show when={props.timestamp || props.status === 'sending' || props.status === 'failed'}>
          <div class="f4-chat-row__meta">
            <Show when={props.timestamp}>{props.timestamp}</Show>
            <Show when={props.status === 'sending'}>
              <Spinner size="sm" label={props.statusLabel ?? ''} />
            </Show>
            <Show when={props.status === 'failed'}>
              <span class="f4-chat-row__status--failed">{props.statusLabel}</span>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
}
